import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import { Confirm } from '@/components/templates/Confirm.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Caption, Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress'
import { patientDetailsPath, selectDateTimePath } from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

const ConfirmBooking = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'confirmBooking',
  })
  const navigate = useNavigate()
  const { appointmentType, category, doctorId, locationId } = useParams()
  const { selectedSlot, setAppointmentType } = useAppointmentStore.getState()

  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.ConfirmBooking,
  )

  const handleGoBack = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      navigate(
        selectDateTimePath({
          appointmentType: appointmentType!,
          category: category!,
          doctorId: doctorId!,
          locationId: locationId!,
        }),
      )
      trackFormInteraction({
        action: 'previous',
        formFieldName: 'back-to-previous',
      })
    },
    [category, appointmentType, locationId, doctorId, navigate],
  )

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb title={t('breadcrumbLink')} onClick={handleGoBack} />
        <Header1>{t('header')}</Header1>
        <Stepper total={6} step={5} />
        <ScrollableContent>
          {selectedSlot && <Confirm slot={selectedSlot} />}
          <Caption>{t('caption')}</Caption>
        </ScrollableContent>
        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            onClick={() => {
              setAppointmentType(appointmentType!)
              navigate(patientDetailsPath())
              trackFormInteraction({
                action: 'next',
                formFieldName: 'confirm-booking',
              })
            }}
          >
            {t('actions.confirmBooking')}
          </StyledActionButton>
          <StyledActionButton variant="Secondary" onClick={handleGoBack}>
            {t('selectDifferentTimeslot')}
          </StyledActionButton>
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { ConfirmBooking }
